import pbjs from 'prebid.js';

import { BordeauxMachineContext, PreBid, PreBidEventTypes, ThirdParty } from '@repo/shared-types';
import { getEnv, getParam } from '@repo/utils';

import './prebidModules';
import { setTargeting } from './setTargeting';
import staticUserSyncIds from './userSyncIds';

const lrh = getParam('lrh');

export const prebid = pbjs as unknown as PreBid;

export const initialise = (
  context: Pick<
    BordeauxMachineContext,
    'liveIntentUserSyncEnabled' | 'config' | 'sommelierResponse' | 'thirdPartyApiConfig'
  >,
): void => {
  const { sellerID } = context.thirdPartyApiConfig[ThirdParty.GLOBAL];
  const { enabled: uid2Enabled } = context.thirdPartyApiConfig[ThirdParty.UID2];
  const { enabled: euidEnabled } = context.thirdPartyApiConfig[ThirdParty.EUID];
  const { liveIntentUserSyncEnabled } = context;

  const TREATMENT_RATE = 0.95;
  if (getEnv().liModuleEnabled === undefined) {
    getEnv().liModuleEnabled = Math.random() < TREATMENT_RATE;
    getEnv().liTreatmentRate = TREATMENT_RATE;
  }

  const auctionsEnriched = {};
  setTargeting();

  const userSyncIds = [
    ...staticUserSyncIds,
    ...(euidEnabled ? [{ name: 'euid', params: { euidCookie: '__euid' } }] : []),
    ...(lrh && uid2Enabled && !pbjs.getUserIds().uid2
      ? [
          {
            name: 'uid2',
            params: {
              serverPublicKey:
                'UID2-X-P-MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAErrdS+bkwKS6LIcrT4BWG7CivsrOds0tGfY667NctMQNtutObZa3Od9Uw5It/GiQJxJ1qD1IO1sdFVqHw11srpA==',
              subscriptionId: 'x2HQwg9gkp',
              emailHash: lrh,
            },
          },
        ]
      : []),
    ...(liveIntentUserSyncEnabled && getEnv().liModuleEnabled
      ? [
          {
            name: 'liveIntentId',
            bidders: [
              'rubicon',
              'pubmatic',
              'ix',
              'ttd',
              'grid',
              'medianet',
              'vidazoo',
              'triplelift',
              'sovrn',
              'openx',
            ],
            params: {
              publisherId: undefined,
              distributorId: 'did-0043',
              requestedAttributesOverrides: {
                uid2: true,
                index: true,
                bidswitch: true,
                medianet: true,
                pubmatic: true,
                magnite: true,
                openx: true,
                sovrn: true,
                thetradedesk: true,
                vidazoo: true,
                triplelift: true,
              },
            },
            storage: {
              type: 'html5',
              name: '__tamLIResolveResult',
              expires: 1,
            },
          },
        ]
      : []),
  ];

  prebid.processQueue();

  prebid.que.push(() => {
    prebid.setConfig({
      paapi: {
        enabled: true,
        defaultForSlots: 1,
      },
    });

    prebid.enableAnalytics([
      {
        provider: 'atsAnalytics',
        options: { pid: '12243' },
      },
    ]);

    prebid.mergeConfig({
      mediaTypePriceGranularity: {
        banner: {
          buckets: [
            { max: 10, increment: 0.01 },
            { max: 20, increment: 0.05 },
            { max: 50, increment: 0.25 },
            { max: 75, increment: 1 },
          ],
        },
        video: {
          buckets: [{ precision: 2, max: 70, increment: 0.1 }],
        },
        native: {
          buckets: [
            { min: 0.05, max: 3.95, increment: 0.05 },
            { min: 4, max: 9.75, increment: 0.25 },
            { min: 10, max: 30, increment: 1 },
          ],
        },
      },
      consentManagement: {
        usp: { cmpApi: 'iab', timeout: 3000 },
        gdpr: {
          cmpApi: 'iab',
          defaultGdprScope: true,
          timeout: 3000,
          rules: [
            {
              purpose: 'storage',
              enforcePurpose: true,
              enforceVendor: true,
              vendorExceptions: ['permutive', 'pubxai'],
            },
            {
              purpose: 'basicAds',
              enforcePurpose: true,
              enforceVendor: true,
              vendorExceptions: [],
            },
          ],
        },
        gpp: { cmpApi: 'iab', timeout: 3000 },
      },
      realTimeData: {
        auctionDelay: 500,
        dataProviders: [
          {
            name: 'permutive',
            waitForIt: true,
            params: { acBidders: ['ix', 'appnexus', 'rubicon', 'pubmatic'] },
          },
        ],
      },
      userSync: {
        auctionDelay: 300,
        syncEnabled: true,
        syncsPerBidder: 3,
        syncDelay: 1000,
        filterSettings: {
          iframe: { bidders: '*', filter: 'include' },
          image: { bidders: '*', filter: 'include' },
        },
        userIds: userSyncIds,
      },
      ...(sellerID && {
        schain: {
          validation: 'strict',
          config: {
            complete: 1,
            ver: '1.0',
            nodes: [{ asi: 'futureplc.com', sid: sellerID, hp: 1 }],
          },
        },
      }),
    });

    prebid.onEvent(PreBidEventTypes.ON_AUCTION, auction => {
      auctionsEnriched[auction.auctionId] =
        auction.adUnits &&
        auction.adUnits.some(
          adUnit =>
            adUnit.bids &&
            adUnit.bids.some(
              bid =>
                bid.userIdAsEids &&
                bid.userIdAsEids.some(
                  eid =>
                    eid.source === 'liveintent.com' ||
                    (eid.uids &&
                      eid.uids.some(uid => uid.ext && uid.ext.provider === 'liveintent.com')),
                ),
            ),
        );
      setTargeting(auctionsEnriched[auction.auctionId]);
    });

    prebid.onEvent<PreBidEventTypes.BID_WON>(PreBidEventTypes.BID_WON, bid => {
      if (bid.dealId) {
        googletag.cmd.push(function () {
          googletag.pubads().setTargeting('dealID', bid.dealId || '');
        });
      }
    });

    prebid.setBidderConfig({
      bidders: ['criteo'],
      config: {
        ortb2: {
          site: {
            publisher: {
              id: '6783',
            },
          },
        },
      },
    });

    prebid.setBidderConfig({
      bidders: ['aps'],
      config: {
        accountID: '3032',
      },
    });

    prebid.bidderSettings = {
      ...prebid.bidderSettings,
      aps: {
        storageAllowed: true,
      },
      standard: {
        storageAllowed: true,
      },
    };
  });
};
